import { FC, useRef } from 'react';

import { CampaignSource } from 'utils/constants/campaign';

import { Flex, Text, TextList, TooltipHover } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import hhLogo16 from 'assets/hh_logo_16.svg';
import vkLogo16 from 'assets/vk_logo_16.svg';
import yaLogo16 from 'assets/ya_logo_16.svg';

type CampaignSourceType = CampaignSource.HH | CampaignSource.VKAds | CampaignSource.Yandex;

const SOURCE_LOGOS_CONFIG: Record<
    CampaignSourceType,
    { image: string; tooltipTrl: string; tooltipTrlList?: string[] }
> = {
    [CampaignSource.Yandex]: {
        image: yaLogo16,
        tooltipTrl: 'CampaignsDashboard.CampaignsTable.CampaignSources.yandex',
    },
    [CampaignSource.VKAds]: {
        image: vkLogo16,
        tooltipTrl: 'CampaignsDashboard.CampaignsTable.CampaignSources.vk',
        tooltipTrlList: [
            'CampaignsDashboard.CampaignsTable.CampaignSources.vk.li1',
            'CampaignsDashboard.CampaignsTable.CampaignSources.vk.li2',
            'CampaignsDashboard.CampaignsTable.CampaignSources.vk.li3',
            'CampaignsDashboard.CampaignsTable.CampaignSources.vk.li4',
        ],
    },
    [CampaignSource.HH]: {
        image: hhLogo16,
        tooltipTrl: 'CampaignsDashboard.CampaignsTable.CampaignSources.hh',
    },
};

const CampaignSourceCoponent: FC<{ source: CampaignSourceType }> = ({ source }) => {
    const activatorRef = useRef<HTMLImageElement>(null);
    const trls = useTrls();

    const { image, tooltipTrl, tooltipTrlList } = SOURCE_LOGOS_CONFIG[source];

    const tooltipContent = (
        <>
            <Text>{trls(tooltipTrl)}</Text>
            {tooltipTrlList && (
                <TextList>
                    {tooltipTrlList.map((item) => (
                        <Text key={item}>{trls(item)}</Text>
                    ))}
                </TextList>
            )}
        </>
    );

    return (
        <>
            <img ref={activatorRef} src={image} alt={source} />
            <TooltipHover delay={500} placement="top-center" activatorRef={activatorRef}>
                {tooltipContent}
            </TooltipHover>
        </>
    );
};

export const CampaignSources: FC<{ sources: CampaignSource[] }> = ({ sources }) => {
    return (
        <Flex gap={4}>
            {sources
                .sort((a, b) => a.localeCompare(b))
                .map(
                    (source) =>
                        [CampaignSource.HH, CampaignSource.VKAds, CampaignSource.Yandex].includes(source) && (
                            <CampaignSourceCoponent key={source} source={source as CampaignSourceType} />
                        )
                )}
        </Flex>
    );
};
